import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { isTextSelection, posToDOMRect } from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { BubbleMenu, Editor, EditorContent, useEditor } from '@tiptap/react';
import type { BubbleMenuProps } from '@tiptap/react';
import { StyledDivComponent } from '../dto/StyleCompRepository';
import { DeviceType, TextAlignment } from '../enum/EnumRepository';
import { useEditMode } from '../hooks/useEditMode';
import { paperTextureModal } from '../component/paperTexture';
import { useStandardAlertStore } from '../store/portal/alertStore';
import { useDeviceStore } from '../store/deviceStore';
import {
  parseRichText,
  RICH_TEXT_LINE_HEIGHT,
  RichTextAlignment,
  RichTextSize,
  richTextToPlainText,
  serializeEditorContent,
} from './document';
import { TextSize } from './textSizeExtension';

export const richTextSurfaceCss = css`
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;

  p {
    line-height: 1;
  }

  span[data-size='S'] {
    font-size: var(--rich-text-size-s, ${({ theme }) => theme.fontSize.font_B04});
    line-height: ${RICH_TEXT_LINE_HEIGHT.S};
  }

  span[data-size='M'] {
    line-height: ${RICH_TEXT_LINE_HEIGHT.M};
  }

  span[data-size='L'] {
    font-size: var(--rich-text-size-l, ${({ theme }) => theme.fontSize.font_B02});
    line-height: ${RICH_TEXT_LINE_HEIGHT.L};
  }

  strong {
    font-weight: ${({ theme }) => theme.fontWeight.semi_bold};
  }

  em {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }
`;

export interface ConstrainedRichTextProps {
  value: string;
  onChange: (next: string) => void;
  StyledView: StyledDivComponent;
  placeholder?: string;
  maxLength?: number;
  maxRows?: number;
  alignment?: TextAlignment;
}

export const RichTextView: React.FC<{ value: string }> = ({ value }) => {
  const doc = parseRichText(value);
  return (
    <>
      {doc.content.map((paragraph, paragraphIndex) => (
        <RichTextParagraphView
          key={paragraphIndex}
          $textAlign={paragraph.attrs?.textAlign}
        >
          {(paragraph.content ?? []).map((node, nodeIndex) => {
            if (node.type === 'hardBreak') {
              return <br key={nodeIndex} />;
            }
            const marks = node.marks ?? [];
            let content: React.ReactNode = node.text;
            if (marks.some((mark) => mark.type === 'bold')) {
              content = <strong>{content}</strong>;
            }
            if (marks.some((mark) => mark.type === 'italic')) {
              content = <em>{content}</em>;
            }
            if (marks.some((mark) => mark.type === 'underline')) {
              content = <u>{content}</u>;
            }
            const textSize = marks.find((mark) => mark.type === 'textSize')?.attrs?.size;
            const size = textSize === 'S' || textSize === 'L' ? textSize : 'M';
            content = <span data-size={size}>{content}</span>;
            return <React.Fragment key={nodeIndex}>{content}</React.Fragment>;
          })}
        </RichTextParagraphView>
      ))}
    </>
  );
};

const RichTextParagraphView = styled.p<{ $textAlign?: RichTextAlignment }>`
  min-height: 1em;
  margin: 0;
  text-align: ${({ $textAlign }) => $textAlign ?? 'inherit'};
`;

const buildExtensions = (placeholder: string) => [
  Document,
  Paragraph,
  Text,
  TextAlign.configure({
    types: ['paragraph'],
    alignments: ['left', 'center', 'right'],
    defaultAlignment: null,
  }),
  Bold,
  Italic,
  Underline,
  History,
  TextSize,
  HardBreak,
  Placeholder.configure({
    placeholder,
    emptyEditorClass: 'is-editor-empty',
    emptyNodeClass: 'is-empty',
    showOnlyWhenEditable: true,
  }),
];

type SelectionTextSize = RichTextSize | 'mixed';
type BubbleMenuShouldShow = NonNullable<BubbleMenuProps['shouldShow']>;
type BubbleMenuTippyOptions = NonNullable<BubbleMenuProps['tippyOptions']>;
type TippyInstance = Parameters<NonNullable<BubbleMenuTippyOptions['onCreate']>>[0];
const TEXT_ALIGNMENTS: RichTextAlignment[] = ['left', 'center', 'right'];
const TEXT_SIZES: RichTextSize[] = ['S', 'M', 'L'];

const toRichTextAlignment = (alignment: TextAlignment): RichTextAlignment => {
  if (alignment === TextAlignment.CENTER) return 'center';
  if (alignment === TextAlignment.RIGHT) return 'right';
  return 'left';
};

const getSelectionTextSize = (editor: Editor): SelectionTextSize => {
  const { from, to, empty } = editor.state.selection;
  if (empty) {
    if (editor.isActive('textSize', { size: 'S' })) return 'S';
    if (editor.isActive('textSize', { size: 'L' })) return 'L';
    return 'M';
  }

  let hasSmall = false;
  let hasLarge = false;
  let hasDefault = false;
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText || !node.text) return;
    const textSize = node.marks.find((mark) => mark.type.name === 'textSize')?.attrs.size;
    if (textSize === 'S') hasSmall = true;
    else if (textSize === 'L') hasLarge = true;
    else hasDefault = true;
  });

  if ([hasSmall, hasDefault, hasLarge].filter(Boolean).length > 1) return 'mixed';
  if (hasSmall) return 'S';
  return hasLarge ? 'L' : 'M';
};

const FALLBACK_BUBBLE_WIDTH = 240;

const toClientRect = (
  left: number,
  top: number,
  width: number,
  height: number,
): DOMRect => ({
  x: left,
  y: top,
  left,
  top,
  width,
  height,
  right: left + width,
  bottom: top + height,
  toJSON: () => ({}),
});

const isAtLineEnd = (editor: Editor, to: number): boolean => {
  const maxPos = editor.state.doc.content.size;
  if (to >= maxPos) return true;
  try {
    const current = editor.view.coordsAtPos(to);
    const next = editor.view.coordsAtPos(Math.min(to + 1, maxPos));
    return next.top >= current.bottom - 1;
  } catch {
    return true;
  }
};

const KEYBOARD_OPEN_PX = 80;
const IOS_KEYBOARD_ACCESSORY_PX = 44;
const KEYBOARD_DOCK_GAP_PX = 8;

let activeMobileToolbarId: string | null = null;
const mobileToolbarListeners = new Set<() => void>();
let fixedUsesLayoutViewport: boolean | null = null;

const claimMobileToolbar = (id: string | null) => {
  activeMobileToolbarId = id;
  mobileToolbarListeners.forEach((notify) => notify());
};

const isAppleTouchDevice = () => {
  const nav = navigator as Navigator & { platform?: string };
  const ua = nav.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return nav.platform === 'MacIntel' && nav.maxTouchPoints > 1;
};

const fixedTopIsLayoutViewport = () => {
  const viewport = window.visualViewport;
  if (!viewport || viewport.offsetTop < 1) return fixedUsesLayoutViewport ?? true;
  if (fixedUsesLayoutViewport !== null) return fixedUsesLayoutViewport;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;pointer-events:none';
  document.body.appendChild(probe);
  const top = probe.getBoundingClientRect().top;
  probe.remove();
  fixedUsesLayoutViewport = Math.abs(top + viewport.offsetTop) <= Math.abs(top) + 1;
  return fixedUsesLayoutViewport;
};

const keyboardDockTop = (barHeight: number) => {
  const viewport = window.visualViewport;
  if (!viewport) return Math.max(0, window.innerHeight - barHeight - KEYBOARD_DOCK_GAP_PX);
  const visualBottom = viewport.offsetTop + viewport.height;
  const coveredBelow = Math.max(0, window.innerHeight - visualBottom);
  const keyboardOpen = coveredBelow > KEYBOARD_OPEN_PX;
  const accessory = keyboardOpen && isAppleTouchDevice() ? IOS_KEYBOARD_ACCESSORY_PX : 0;
  const origin = fixedTopIsLayoutViewport() ? viewport.offsetTop : 0;
  return Math.max(0, origin + viewport.height - barHeight - accessory - KEYBOARD_DOCK_GAP_PX);
};

const getBubbleTippyOptions = (
  editor: Editor,
  toolbarRef: React.RefObject<HTMLDivElement>,
  tippyInstanceRef: React.MutableRefObject<TippyInstance | undefined>,
) => ({
  duration: 120,
  placement: 'top-start' as const,
  offset: [0, 6] as [number, number],
  onCreate: (instance: TippyInstance) => {
    tippyInstanceRef.current = instance;
  },
  onDestroy: (instance: TippyInstance) => {
    if (tippyInstanceRef.current === instance) {
      tippyInstanceRef.current = undefined;
    }
  },
  popperOptions: {
    modifiers: [
      { name: 'flip', enabled: false },
      { name: 'preventOverflow', enabled: false },
    ],
  },
  getReferenceClientRect: () => {
    const { from, to } = editor.state.selection;
    const selection = posToDOMRect(editor.view, from, to);
    const area = editor.view.dom.getBoundingClientRect();
    const bubbleWidth = toolbarRef.current?.offsetWidth || FALLBACK_BUBBLE_WIDTH;
    const overflowsArea = selection.left + bubbleWidth > area.right;
    const overflowsLineEnd = isAtLineEnd(editor, to) && selection.left + bubbleWidth > selection.right;
    if (!overflowsArea && !overflowsLineEnd) {
      return selection;
    }
    return toClientRect(
      Math.max(area.left, selection.right - bubbleWidth),
      selection.top,
      bubbleWidth,
      selection.height,
    );
  },
});

interface RichTextToolbarProps {
  editor: Editor;
  currentAlignment: RichTextAlignment;
  selectionSize: SelectionTextSize;
  toolbarRef?: React.RefObject<HTMLDivElement>;
  mobile?: boolean;
}

const RichTextToolbar: React.FC<RichTextToolbarProps> = ({
  editor,
  currentAlignment,
  selectionSize,
  toolbarRef,
  mobile = false,
}) => {
  const preserveSelection = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Toolbar
      ref={toolbarRef}
      role="toolbar"
      aria-label="Text formatting"
    >
      <MarkButton
        type="button"
        $active={editor.isActive('bold')}
        $kind="bold"
        $mobile={mobile}
        onPointerDown={preserveSelection}
        onClick={() => editor.chain().focus().toggleBold().run()}
        aria-label="Bold"
        aria-pressed={editor.isActive('bold')}
      >
        A
      </MarkButton>
      <MarkButton
        type="button"
        $active={editor.isActive('italic')}
        $kind="italic"
        $mobile={mobile}
        onPointerDown={preserveSelection}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        aria-label="Italic"
        aria-pressed={editor.isActive('italic')}
      >
        A
      </MarkButton>
      <MarkButton
        type="button"
        $active={editor.isActive('underline')}
        $kind="underline"
        $mobile={mobile}
        onPointerDown={preserveSelection}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        aria-label="Underline"
        aria-pressed={editor.isActive('underline')}
      >
        A
      </MarkButton>
      <ToolbarDivider />
      {TEXT_ALIGNMENTS.map((textAlign) => (
        <MarkButton
          key={textAlign}
          type="button"
          $active={currentAlignment === textAlign}
          $kind="alignment"
          $mobile={mobile}
          onPointerDown={preserveSelection}
          onClick={() => editor.chain().focus().setTextAlign(textAlign).run()}
          aria-label={`Align ${textAlign}`}
          aria-pressed={currentAlignment === textAlign}
        >
          <AlignmentIcon $textAlign={textAlign} aria-hidden="true">
            <span />
            <span />
            <span />
          </AlignmentIcon>
        </MarkButton>
      ))}
      <ToolbarDivider />
      {TEXT_SIZES.map((size) => (
        <MarkButton
          key={size}
          type="button"
          $active={selectionSize === size}
          $kind="size"
          $mobile={mobile}
          onPointerDown={preserveSelection}
          onClick={() => {
            if (size === 'M') {
              editor.chain().focus().unsetTextSize().run();
              return;
            }
            editor.chain().focus().setTextSize(size).run();
          }}
          aria-label={`${size === 'S' ? 'Small' : size === 'M' ? 'Medium' : 'Large'} text`}
          aria-pressed={selectionSize === size}
        >
          {size}
        </MarkButton>
      ))}
    </Toolbar>
  );
};

const ConstrainedRichTextEditor: React.FC<ConstrainedRichTextProps> = ({
  value,
  onChange,
  StyledView,
  placeholder = '',
  maxLength,
  maxRows,
  alignment = TextAlignment.LEFT,
}) => {
  const { setTextLengthExceededAlert } = useStandardAlertStore();
  const isMobile = useDeviceStore((state) => state.deviceType === DeviceType.MOBILE);
  const editorId = useId();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const tippyInstanceRef = useRef<TippyInstance>();
  const isMobileRef = useRef(isMobile);
  const blurTimerRef = useRef<number>();
  const [mobileToolbarMounted, setMobileToolbarMounted] = useState(false);
  const [mobileToolbarOwnerId, setMobileToolbarOwnerId] = useState(activeMobileToolbarId);
  const [keyboardDockTopPx, setKeyboardDockTopPx] = useState(0);

  const bubbleMenuShouldShow = useRef<BubbleMenuShouldShow>(({
    editor: menuEditor,
    element,
    view,
    state,
    from,
    to,
  }) => {
    if (isMobileRef.current) return false;

    const { doc, selection } = state;
    const isEmptyTextBlock = !doc.textBetween(from, to).length
      && isTextSelection(selection);
    const isChildOfMenu = element.contains(document.activeElement);
    const hasEditorFocus = view.hasFocus() || isChildOfMenu;

    return hasEditorFocus
      && !selection.empty
      && !isEmptyTextBlock
      && menuEditor.isEditable;
  }).current;

  const editor = useEditor({
    extensions: buildExtensions(placeholder),
    content: parseRichText(value),
    immediatelyRender: true,
    onUpdate: ({ editor: nextEditor }) => {
      const next = serializeEditorContent(nextEditor.getJSON());
      const plainLength = richTextToPlainText(next).length;
      if (maxLength && plainLength > maxLength) {
        nextEditor.commands.undo();
        setTextLengthExceededAlert(maxLength);
        return;
      }
      if (next !== value) {
        onChange(next);
      }
    },
    onFocus: () => {
      window.clearTimeout(blurTimerRef.current);
      claimMobileToolbar(editorId);
      setMobileToolbarMounted(true);
    },
    onBlur: () => {
      blurTimerRef.current = window.setTimeout(() => {
        setMobileToolbarMounted(false);
        if (activeMobileToolbarId === editorId) claimMobileToolbar(null);
      }, 240);
    },
  }, [placeholder]);

  useLayoutEffect(() => {
    isMobileRef.current = isMobile;
    if (isMobile) tippyInstanceRef.current?.hide();
  }, [isMobile]);

  useEffect(() => {
    const notify = () => setMobileToolbarOwnerId(activeMobileToolbarId);
    mobileToolbarListeners.add(notify);
    return () => {
      mobileToolbarListeners.delete(notify);
      window.clearTimeout(blurTimerRef.current);
      if (activeMobileToolbarId === editorId) claimMobileToolbar(null);
    };
  }, [editorId]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const incoming = serializeEditorContent(parseRichText(value));
    const current = serializeEditorContent(editor.getJSON());
    if (incoming === current) return;
    editor.commands.setContent(parseRichText(value), false);
  }, [editor, value]);

  const plainLength = editor
    ? editor.getText({ blockSeparator: '\n' }).length
    : richTextToPlainText(value).length;
  const selectionSize = editor ? getSelectionTextSize(editor) : 'M';
  const selectionAlignment = editor
    ? TEXT_ALIGNMENTS.find((textAlign) => editor.isActive({ textAlign }))
    : undefined;
  const currentAlignment = selectionAlignment ?? toRichTextAlignment(alignment);
  const showMobileToolbar = isMobile
    && mobileToolbarMounted
    && mobileToolbarOwnerId === editorId
    && typeof document !== 'undefined';

  useLayoutEffect(() => {
    if (!showMobileToolbar) return;
    const updateDock = () => {
      setKeyboardDockTopPx(keyboardDockTop(dockRef.current?.offsetHeight || 34));
    };
    updateDock();
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updateDock);
    viewport?.addEventListener('scroll', updateDock);
    window.addEventListener('resize', updateDock);
    return () => {
      viewport?.removeEventListener('resize', updateDock);
      viewport?.removeEventListener('scroll', updateDock);
      window.removeEventListener('resize', updateDock);
    };
  }, [showMobileToolbar]);

  return (
    <Root $maxRows={maxRows}>
      {editor && showMobileToolbar && createPortal(
        <MobileKeyboardDock ref={dockRef} style={{ top: keyboardDockTopPx }}>
          <RichTextToolbar
            editor={editor}
            currentAlignment={currentAlignment}
            selectionSize={selectionSize}
            mobile
          />
        </MobileKeyboardDock>,
        document.body,
      )}
      {maxLength && (
        <LimitDiv>
          {plainLength} / {maxLength}
        </LimitDiv>
      )}
      <StyledView $alignment={alignment} $isEditMode>
        {editor && (
          <BubbleMenu
            editor={editor}
            shouldShow={bubbleMenuShouldShow}
            tippyOptions={getBubbleTippyOptions(editor, toolbarRef, tippyInstanceRef)}
          >
            <RichTextToolbar
              editor={editor}
              currentAlignment={currentAlignment}
              selectionSize={selectionSize}
              toolbarRef={toolbarRef}
            />
          </BubbleMenu>
        )}
        <EditorContent editor={editor} />
      </StyledView>
    </Root>
  );
};

const ConstrainedRichText: React.FC<ConstrainedRichTextProps> = (props) => {
  const { isEditMode } = useEditMode();
  const {
    value,
    StyledView,
    alignment = TextAlignment.LEFT,
  } = props;

  if (!isEditMode) {
    return (
      <Root>
        <StyledView $alignment={alignment} $isEditMode={false}>
          <RichTextView value={value} />
        </StyledView>
      </Root>
    );
  }

  return <ConstrainedRichTextEditor {...props} />;
};

const Root = styled.div<{ $maxRows?: number }>`
  position: relative;
  width: 100%;
  ${richTextSurfaceCss}

  .ProseMirror {
    outline: none;
    min-height: 1.2em;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    word-break: break-word;
    text-align: inherit;
    ${({ $maxRows }) => $maxRows ? `max-height: calc(1.6em * ${$maxRows}); overflow-y: auto;` : ''}
  }

  .ProseMirror p {
    margin: 0;
  }

  .ProseMirror p.is-editor-empty,
  .ProseMirror p.is-empty {
    line-height: ${RICH_TEXT_LINE_HEIGHT.M};
  }

  .ProseMirror p.is-editor-empty:first-child::before,
  .ProseMirror p.is-empty:first-child::before {
    content: attr(data-placeholder);
    color: ${({ theme }) => theme.colors.color_Gray_05};
    pointer-events: none;
    float: left;
    height: 0;
  }

  .tippy-box {
    background: transparent;
  }
`;

const MobileKeyboardDock = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  width: max-content;
  max-width: calc(100vw - 24px);
`;

const LimitDiv = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  z-index: 2;
`;

const Toolbar = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px 4px;
  ${paperTextureModal}
`;

const ToolbarDivider = styled.span`
  width: 1px;
  height: 12px;
  margin: 2px;
  background-color: ${({ theme }) => theme.colors.color_Gray_05};
`;

const MarkButton = styled.button<{
  $active: boolean;
  $kind: 'bold' | 'italic' | 'underline' | 'size' | 'alignment';
  $mobile: boolean;
}>`
  min-width: ${({ $mobile }) => $mobile ? '26px' : '22px'};
  height: ${({ $mobile }) => $mobile ? '26px' : '22px'};
  padding: ${({ $kind, $mobile }) => {
    if ($mobile && $kind === 'size') return '0 4px';
    if ($mobile && $kind === 'alignment') return '0 5px';
    if ($mobile) return '0 7px';
    if ($kind === 'size') return '0 3px 0 5px';
    if ($kind === 'alignment') return '0 4px';
    return '0 5px';
  }};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.colors.color_Gray_03 : theme.colors.color_Gray_04)};
  line-height: 1;
  font-weight: ${({ $kind, theme }) => (
    $kind === 'bold' ? theme.fontWeight.semi_bold : theme.fontWeight.regular
  )};
  font-style: ${({ $kind }) => ($kind === 'italic' ? 'italic' : 'normal')};
  text-decoration: ${({ $kind }) => ($kind === 'underline' ? 'underline' : 'none')};
  text-underline-offset: ${({ $kind }) => ($kind === 'underline' ? '2px' : '0')};
  font-size: ${({ theme }) => theme.fontSize.font_B04};
  cursor: pointer;
  touch-action: manipulation;
`;

const AlignmentIcon = styled.span<{ $textAlign: RichTextAlignment }>`
  width: 14px;
  height: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: ${({ $textAlign }) => {
    if ($textAlign === 'left') return 'flex-start';
    if ($textAlign === 'right') return 'flex-end';
    return 'center';
  }};

  span {
    height: 1px;
    background: currentColor;
  }

  span:nth-child(1) {
    width: 14px;
  }

  span:nth-child(2) {
    width: 9px;
  }

  span:nth-child(3) {
    width: 12px;
  }
`;

export default ConstrainedRichText;
